// src/pages/Courses/CourseList.js
import React, { useState, useEffect, useCallback } from "react";
import { Card, Form, Dropdown, Row, Col } from "react-bootstrap";
import { Link } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import DataTable from "react-data-table-component";
import { search } from "react-icons-kit/fa/search";
import Layout from "../../layout/default";
import Block from "../../components/Block/Block";
import { Icon } from "../../components";
import { API_BASE_URL } from "../../services/apiBase";

function CourseList() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedRows, setSelectedRows] = useState([]);

  const authToken = localStorage.getItem("authToken");

  const fetchCourses = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const response = await axios.get(`${API_BASE_URL}/api/courses/`, {
          params: { page, per_page: perPage, search: searchQuery },
          headers: { Authorization: `Bearer ${authToken}` },
        });

        console.log('📌 Courses API Response:', response.data);

        const res = response.data;
        // Backend returns: { success, message, data: [...], pagination: { total, ... } }
        const data = res?.data || res?.results || [];
        const total = res?.pagination?.total || res?.count || data.length;

        console.log('📌 Courses extracted:', data.length, 'Total:', total);

        setCourses(data);
        setCurrentPage(page);
        setTotalCount(total);
      } catch (error) {
        setCourses([]);
        setTotalCount(0);
        Swal.fire(
          "Error!",
          error.response?.data?.error ||
            error.response?.data?.message ||
            "Failed to fetch courses.",
          "error"
        );
      } finally {
        setLoading(false);
      }
    },
    [authToken, perPage, searchQuery]
  );

  useEffect(() => {
    fetchCourses(currentPage);
  }, [currentPage, fetchCourses]);

  const handlePageChange = (page) => setCurrentPage(page);
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchCourses(1);
  };
  const handleSelectedRowsChange = ({ selectedRows }) =>
    setSelectedRows(selectedRows);

  const deleteConfirm = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You want to delete this course?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
    });

    if (!result.isConfirmed) return;

    try {
      const response = await axios.delete(
        `${API_BASE_URL}/api/courses/${id}/`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      if (response.status === 200 || response.status === 204) {
        Swal.fire("Deleted!", "Course deleted successfully.", "success");
        fetchCourses(currentPage);
      }
    } catch (error) {
      Swal.fire(
        "Error!",
        error.response?.data?.message || "Failed to delete.",
        "error"
      );
    }
  };

  const columns = [
    {
      name: "Thumbnail",
      selector: (row) => row.thumbnail ?? "",
      cell: (row) => (
        <img
          src={row.thumbnail ?? "/placeholder.png"}
          alt={row.title}
          style={{
            width: "60px",
            height: "45px",
            objectFit: "cover",
            borderRadius: "5px",
          }}
        />
      ),
    },
    { name: "Course ID", selector: (row) => row.course_id ?? "—", sortable: true },
    { name: "Title", selector: (row) => row.title ?? "—", sortable: true },
    {
      name: "Difficulty",
      selector: (row) => row.difficulty_level ?? "—",
      sortable: true,
      cell: (row) => (
        <span className={`badge bg-${
          row.difficulty_level === 'beginner' ? 'info' :
          row.difficulty_level === 'intermediate' ? 'primary' : 'danger'
        }`}>
          {row.difficulty_level}
        </span>
      )
    },
    {
      name: "Status",
      selector: (row) => row.status ?? "—",
      sortable: true,
      cell: (row) => (
        <span className={`badge bg-${
          row.status === 'published' ? 'success' :
          row.status === 'draft' ? 'warning' : 'secondary'
        }`}>
          {row.status}
        </span>
      )
    },
    { name: "Duration (hrs)", selector: (row) => row.duration_hours ?? 0, sortable: true },
    {
      name: "Featured",
      selector: (row) => row.is_featured ? "Yes" : "No",
      sortable: true,
      cell: (row) => row.is_featured ? <span className="badge bg-warning">Featured</span> : "—"
    },
    {
      name: "Created At",
      selector: (row) =>
        row.created_at ? new Date(row.created_at).toLocaleDateString() : "—",
      sortable: true,
    },
    {
      name: "Actions",
      cell: (row) => (
        <Dropdown align="end">
          <Dropdown.Toggle variant="link" className="btn btn-sm btn-icon btn-zoom me-n1">
            <Icon name="more-v" />
          </Dropdown.Toggle>
          <Dropdown.Menu className="dropdown-menu-end shadow-sm">
            <Dropdown.Item as={Link} to={`/Courses/view-course/${row.id}`}>
              <Icon name="eye" className="me-2" /> View
            </Dropdown.Item>
            <Dropdown.Item as={Link} to={`/Courses/update-course/${row.id}`}>
              <Icon name="edit" className="me-2" /> Edit
            </Dropdown.Item>
            <Dropdown.Item onClick={() => deleteConfirm(row.id)}>
              <Icon name="trash" className="me-2" /> Delete
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      isButton: true,
    },
  ];

  const customStyles = {
    headRow: {
      style: {
        background: "linear-gradient(to right, #e0eafc, #cfdef3)",
        fontWeight: "bold",
        fontSize: "14px",
      },
    },
    rows: {
      style: {
        backgroundColor: "#f8f9fa",
        borderBottom: "1px solid #dee2e6",
        "&:hover": { backgroundColor: "#e6f0ff" },
      },
    },
    headCells: { style: { color: "#333" } },
    cells: { style: { paddingTop: "12px", paddingBottom: "12px" } },
  };

  return (
    <Layout title="Courses" content="container">
      <Block.Head>
        <Block.HeadBetween>
          <Block.HeadContent>
            <Block.Title tag="h2">Courses</Block.Title>
            <p className="text-muted">Manage all courses in the system</p>
          </Block.HeadContent>
          <Block.HeadContent>
            <Link to="/Courses/create-course" className="btn btn-primary ms-2">
              <Icon name="plus me-1" /> Add Course
            </Link>
          </Block.HeadContent>
        </Block.HeadBetween>
      </Block.Head>

      <Block>
        <Card className="p-3">
          <Form onSubmit={handleSearchSubmit} className="mb-3 narrow-search">
            <Row className="align-items-center g-2">
              <Col md={10}>
                <Form.Control
                  type="text"
                  placeholder="Search by title, description, or college"
                  value={searchQuery}
                  onChange={handleSearchChange}
                />
              </Col>
              <Col md={2}>
                <button className="btn btn-outline-primary" type="submit">
                  <Icon icon={search} /> Search
                </button>
              </Col>
            </Row>
          </Form>

          <DataTable
            tableClassName="data-table-head-light table-responsive data-table-checkbox"
            data={courses}
            columns={columns}
            customStyles={customStyles}
            selectableRows
            onSelectedRowsChange={handleSelectedRowsChange}
            selectedRows={selectedRows}
            progressPending={loading}
            pagination
            paginationServer
            paginationTotalRows={totalCount}
            paginationPerPage={perPage}
            onChangePage={handlePageChange}
          />
        </Card>
      </Block>
    </Layout>
  );
}

export default CourseList;
